import type { CSSProperties, ReactNode, MouseEvent } from "react";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import MuiButton from "@mui/material/Button";
import IconButton from "@mui/material/IconButton";
import CloseIcon from "@mui/icons-material/Close";
import { confirmDialog } from "../../feedback/confirmDialog";

// Thin wrapper mapping antd <Modal> onto MUI Dialog so call sites swap by import
// only and keep feature parity: title, open/visible, onOk/onCancel, footer
// (null = hide, custom node = replace default OK/Cancel), okText/cancelText,
// width, confirmLoading, closable. Modal.confirm delegates to confirmDialog.

type AntdEvent = MouseEvent<HTMLElement>;

export interface ModalProps {
  open?: boolean;
  /** antd v4 alias for open. */
  visible?: boolean;
  title?: ReactNode;
  children?: ReactNode;
  onOk?: (e?: AntdEvent) => void | Promise<unknown>;
  onCancel?: (e?: AntdEvent) => void;
  footer?: ReactNode | null;
  okText?: ReactNode;
  cancelText?: ReactNode;
  okButtonProps?: { disabled?: boolean; danger?: boolean; [k: string]: unknown };
  cancelButtonProps?: { disabled?: boolean; [k: string]: unknown };
  confirmLoading?: boolean;
  width?: number | string;
  closable?: boolean;
  centered?: boolean;
  destroyOnClose?: boolean;
  maskClosable?: boolean;
  zIndex?: number;
  afterClose?: () => void;
  className?: string;
  style?: CSSProperties;
  "data-testid"?: string;
  "data-cy"?: string;
}

export function Modal({
  open,
  visible,
  title,
  children,
  onOk,
  onCancel,
  footer,
  okText = "OK",
  cancelText = "Cancel",
  okButtonProps,
  cancelButtonProps,
  confirmLoading,
  width = 520,
  closable = true,
  // antd `centered` vertically centers the modal; MUI Dialog is already
  // vertically centered by default, so this is accepted as a no-op for parity.
  centered,
  zIndex,
  afterClose,
  maskClosable = true,
  className,
  style,
  "data-testid": dataTestid,
  "data-cy": dataCy,
}: ModalProps) {
  const isOpen = open ?? visible ?? false;
  const handleClose = (_e: unknown, reason?: string) => {
    if (reason === "backdropClick" && maskClosable === false) return;
    onCancel?.();
  };

  return (
    <Dialog
      open={isOpen}
      onClose={handleClose}
      maxWidth={false}
      className={className}
      style={style}
      // antd afterClose fires after the close transition completes — callers use it
      // to reset modal state once it's fully hidden.
      TransitionProps={afterClose ? { onExited: afterClose } : undefined}
      {...(zIndex != null ? { sx: { zIndex } } : {})}
      // Forward antd data-cy/data-testid onto the dialog Paper (the box that
      // holds title/content/actions) so `[data-cy=...]` scopes still work,
      // matching antd's .ant-modal which carried these attrs.
      PaperProps={{ sx: { width, maxWidth: "95vw" }, "data-cy": dataCy, "data-testid": dataTestid }}
    >
      {title != null && (
        <DialogTitle sx={{ pr: closable ? 6 : 3 }}>
          {title}
          {closable && (
            <IconButton
              aria-label="Close"
              onClick={() => onCancel?.()}
              sx={{ position: "absolute", right: 8, top: 8 }}
            >
              <CloseIcon />
            </IconButton>
          )}
        </DialogTitle>
      )}
      <DialogContent>{children}</DialogContent>
      {footer === null ? null : footer !== undefined ? (
        <DialogActions>{footer}</DialogActions>
      ) : (
        <DialogActions>
          <MuiButton onClick={() => onCancel?.()} color="inherit" disabled={cancelButtonProps?.disabled}>
            {cancelText}
          </MuiButton>
          <MuiButton
            onClick={(e) => onOk?.(e)}
            variant="contained"
            color={okButtonProps?.danger ? "error" : "primary"}
            disabled={okButtonProps?.disabled || confirmLoading}
          >
            {okText}
          </MuiButton>
        </DialogActions>
      )}
    </Dialog>
  );
}

// antd Modal.confirm(...) -> promise-based confirmDialog (returns void to match antd).
Modal.confirm = (opts: {
  title?: ReactNode;
  content?: ReactNode;
  okText?: string;
  cancelText?: string;
  okType?: string;
  okButtonProps?: { danger?: boolean; [k: string]: unknown };
  onOk?: () => void | Promise<unknown>;
  onCancel?: () => void;
}) => {
  void confirmDialog({
    title: opts.title,
    content: opts.content,
    confirmText: opts.okText,
    cancelText: opts.cancelText,
    danger: opts.okType === "danger" || opts.okButtonProps?.danger === true,
  }).then((ok) => {
    if (ok) opts.onOk?.();
    else opts.onCancel?.();
  });
};

export default Modal;
